/**
 * §6.3 — category header display.
 *
 * "Text is scaled up to fill the header cell, not merely scaled down to avoid
 * overflow." That distinction is the whole component: a naive shrink-to-fit
 * leaves a short name rendering at the same size as a long one, and §6.3
 * requires each header to be sized independently so `Bonds` runs huge while
 * `Florida Pension Law` wraps and stays readable.
 *
 * Requirements met here:
 *   - grows as well as shrinks
 *   - wraps across lines when that permits a larger size than one line
 *   - never truncates, abbreviates, or ellipsizes
 *   - recomputes on resize and after webfonts finish loading
 */

import {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  type CSSProperties,
  type ReactNode,
} from "react";

/**
 * Sizes a set of FitTexts together: each is measured independently, and every
 * one then renders at the smallest size any of them needed.
 *
 * §6.3 asks for headers "scaled up to fill the header cell", sized
 * independently, and read on its own that is what the bare component does. On a
 * real six-category board the result is ransom-note typography — `Investments`
 * renders at roughly twice `Florida Pension Law`, purely because it is one short
 * word, and the row stops reading as a band. Worse, it implies a hierarchy the
 * categories do not have: the biggest word looks like the important one.
 *
 * A shared size keeps the part of §6.3 that matters. The band still GROWS to
 * fill — it is not shrink-to-fit — it just grows to the largest size that fits
 * every cell rather than each cell separately. Nothing is truncated or
 * abbreviated, and a long name still wraps.
 *
 * Without a provider above them, FitTexts size individually exactly as before,
 * which is right for the clue card: it is one piece of text on the screen with
 * nothing to agree with.
 */
interface FitGroup {
  /** A member's own best size, once measured. */
  report(id: number, size: number): void;
  /** Registers the callback that applies the agreed size. Returns an unsubscribe. */
  join(id: number, apply: (size: number) => void): () => void;
}

const FitTextGroupContext = createContext<FitGroup | null>(null);

let nextFitTextId = 0;

export function FitTextGroup({ children }: { children: ReactNode }): React.ReactElement {
  const sizes = useRef(new Map<number, number>());
  const appliers = useRef(new Map<number, (size: number) => void>());

  const group = useMemo<FitGroup>(() => {
    const publish = () => {
      const measured = [...sizes.current.values()].filter((size) => size > 0);
      if (measured.length === 0) return;
      const agreed = Math.min(...measured);
      for (const apply of appliers.current.values()) apply(agreed);
    };

    return {
      report(id, size) {
        if (sizes.current.get(id) === size) return;
        sizes.current.set(id, size);
        // Every member re-applies, not just the reporter: one cell measuring
        // smaller has to pull the others down with it.
        publish();
      },
      join(id, apply) {
        appliers.current.set(id, apply);
        return () => {
          appliers.current.delete(id);
          // Dropped from the vote too — a removed category must not keep
          // holding the whole band at its size.
          sizes.current.delete(id);
        };
      },
    };
  }, []);

  return <FitTextGroupContext.Provider value={group}>{children}</FitTextGroupContext.Provider>;
}

export interface FitTextProps {
  children: ReactNode;
  /** Smallest size to consider, in pixels. */
  min?: number;
  /** Largest size to consider, in pixels. */
  max?: number;
  /** Line height multiplier, applied during measurement and rendering alike. */
  lineHeight?: number;
  className?: string;
  style?: CSSProperties;
  /** Applied to the inner span. Must not include a transform — use `condense`. */
  textStyle?: CSSProperties;
  /**
   * The condensed lettering the board uses, as a scale factor per axis.
   *
   * It lives here rather than in `textStyle` because a CSS transform does not
   * affect `scrollWidth`/`scrollHeight`: text measured to fit and *then* scaled
   * to 1.2× vertically overflows its cell, which §6.3 forbids. Passing it in
   * lets the fit budget account for it.
   */
  condense?: { x: number; y: number };
  as?: "div" | "span" | "h1" | "h2" | "p";
}

export function FitText({
  children,
  min = 8,
  max = 400,
  lineHeight = 1.04,
  className,
  style,
  textStyle,
  condense,
  as: Tag = "div",
}: FitTextProps): React.ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const group = useContext(FitTextGroupContext);
  const idRef = useRef(-1);
  if (idRef.current === -1) idRef.current = nextFitTextId += 1;

  useLayoutEffect(() => {
    const container = containerRef.current;
    const text = textRef.current;
    if (!container || !text) return;

    // Registered before the first measurement, so a size published by a sibling
    // that measures first is not missed.
    const leave = group?.join(idRef.current, (size) => {
      text.style.fontSize = `${Math.floor(size * 10) / 10}px`;
    });

    let frame = 0;
    const scaleX = condense?.x ?? 1;
    const scaleY = condense?.y ?? 1;

    const measure = () => {
      // The content box, not the border box: `getBoundingClientRect()` includes
      // padding and borders the text cannot occupy, and on a bordered header
      // cell that slack is enough to let the last letter render clipped.
      const style = getComputedStyle(container);
      const availableWidth =
        container.clientWidth -
        parseFloat(style.paddingLeft || "0") -
        parseFloat(style.paddingRight || "0");
      const availableHeight =
        container.clientHeight -
        parseFloat(style.paddingTop || "0") -
        parseFloat(style.paddingBottom || "0");
      if (availableWidth < 2 || availableHeight < 2) return;

      // The transform is applied after layout, so the budget the text is
      // measured against is the box divided by the scale it will be drawn at.
      const budgetWidth = availableWidth / scaleX;
      const budgetHeight = availableHeight / scaleY;

      // The transform is removed for the duration of the measurement so the
      // range rectangle below is in untransformed pixels, matching the budget.
      const appliedTransform = text.style.transform;
      text.style.transform = "none";

      // Measured from the text's own line boxes rather than from scrollWidth: a
      // word too long to wrap overflows its block as ink, and scrollWidth
      // reports it as fitting. That is what let `Investments` render clipped.
      const range = document.createRange();
      const measureAt = (candidate: number): boolean => {
        text.style.fontSize = `${candidate}px`;
        range.selectNodeContents(text);
        const box = range.getBoundingClientRect();
        return box.width <= budgetWidth + 0.5 && box.height <= budgetHeight + 0.5;
      };

      // Binary search for the largest size that still fits in both dimensions.
      // Wrapping is left to the browser, so a two-line layout is considered on
      // its own merits rather than being ruled out in advance.
      let low = min;
      let high = max;
      let best = min;

      while (high - low > 0.5) {
        const candidate = (low + high) / 2;
        if (measureAt(candidate)) {
          best = candidate;
          low = candidate;
        } else {
          high = candidate;
        }
      }

      range.detach();
      text.style.transform = appliedTransform;

      // The result is written straight to the node. Keeping it out of React
      // state matters: the measurement pass mutates `style.fontSize` directly,
      // and a state-driven value would be diffed against a prop React still
      // believes is applied — so React would skip the write and the text would
      // stay at its unmeasured default.
      text.style.fontSize = `${Math.floor(best * 10) / 10}px`;

      // In a group this is a vote, not the final word: the provider takes the
      // smallest across the set and writes it back through `join` above. This
      // cannot loop — the applied size changes the text's own box, never the
      // container's, so the ResizeObserver below does not fire on it.
      group?.report(idRef.current, best);
    };

    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };

    schedule();

    // §6.3 — recomputed when the display resizes.
    const observer = new ResizeObserver(schedule);
    observer.observe(container);

    // §6.3 — and after webfonts finish loading, since metrics change then.
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    fonts?.ready.then(schedule).catch(() => undefined);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      leave?.();
    };
  }, [children, min, max, lineHeight, condense?.x, condense?.y, group]);

  return (
    <Tag
      ref={containerRef as never}
      className={className}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        ...style,
      }}
    >
      <span
        ref={textRef}
        style={{
          // No fontSize here by design — see the measurement pass above.
          lineHeight,
          // Wrapping is what lets a long name render larger than it could on one
          // line, so it is never disabled and text is never clipped. It breaks
          // between words only: hyphenating `Investment/s` would be a worse
          // reading than the slightly smaller type that fits without it, and
          // §6.3 rules out abbreviating in any case. A single word too long for
          // its box is handled by the search shrinking it instead.
          whiteSpace: "normal",
          overflowWrap: "normal",
          wordBreak: "normal",
          textAlign: "center",
          display: "block",
          maxWidth: "100%",
          ...textStyle,
          ...(condense ? { transform: `scale(${condense.x}, ${condense.y})` } : {}),
        }}
      >
        {children}
      </span>
    </Tag>
  );
}
